import { saveAs } from "file-saver";
import {
  createDocumentMetadataBatch,
  attachFilesToAssignments,
  getNewAssignmentFile,
  deleteAssignmentDocument,
} from "@/utils/newDbUtils";

// The buckets the API splits assignment files into (they come back on the
// assignment payload as adminFiles / sharedFiles / workFiles). Same values the
// spreadsheet upload sends.
export const FILE_ACCESS_TYPES = [
  { label: "Work File", value: "WORK_FILE" },
  { label: "Shared File (visible to staff)", value: "SHARED_FILE" },
  { label: "Admin File (visible to PMs)", value: "ADMIN_FILE" },
];

export const DEFAULT_FILE_ACCESS_TYPE = "WORK_FILE";

export const getFileAccessTypeLabel = (fileAccessType) =>
  FILE_ACCESS_TYPES.find((el) => el.value === fileAccessType)?.label || "";

// The metadata endpoint wants the full file name, the attach payload wants the
// base name and the extension apart, so keep all three.
export const getFileParts = (file) => {
  const fullName = file?.name || "";
  const lastDotIndex = fullName.lastIndexOf(".");
  const hasExtension = lastDotIndex > 0 && lastDotIndex < fullName.length - 1;

  return {
    baseName: hasExtension ? fullName.slice(0, lastDotIndex) : fullName,
    extension: hasExtension ? fullName.slice(lastDotIndex + 1) : "",
    fullName,
  };
};

// A document the user picked but that is not on the server yet — it is held in
// the assignment form until the assignment is saved.
export const createPendingDocument = (
  file,
  fileAccessType = DEFAULT_FILE_ACCESS_TYPE
) => {
  const { baseName, extension, fullName } = getFileParts(file);

  return {
    file,
    fileAccessType,
    fileName: fullName,
    attachmentFileName: baseName,
    fileExtension: extension,
    contentType: file.type || "application/octet-stream",
    uploaded: false,
  };
};

// A document that already lives on the server (returned with the assignment).
export const mapAttachedDocument = (file, fileAccessType) => ({
  id: file?.id ?? null,
  file: null,
  fileAccessType: file?.fileAccessType || fileAccessType,
  fileName: file?.fileExtension
    ? `${file.fileName}.${file.fileExtension}`
    : file?.fileName || "",
  attachmentFileName: file?.fileName ?? null,
  fileExtension: file?.fileExtension ?? null,
  contentType: file?.contentType ?? null,
  uploaded: true,
});

// Pull the already-attached documents off an assignment response into one list.
export const mapAttachedDocuments = (data) =>
  [
    { key: "adminFiles", fileAccessType: "ADMIN_FILE" },
    { key: "sharedFiles", fileAccessType: "SHARED_FILE" },
    { key: "workFiles", fileAccessType: "WORK_FILE" },
  ].flatMap(({ key, fileAccessType }) =>
    (data?.[key] || []).map((file) => mapAttachedDocument(file, fileAccessType))
  );

// The batch endpoint returns the signed URLs in its own order, so pair each
// upload back to the document it belongs to by name (a matched index is never
// reused, which keeps same-named files apart).
const findUploadForDocument = (document, uploads, matchedIndexes) => {
  const candidateNames = [
    document.fileName,
    document.attachmentFileName,
    document.fileExtension
      ? `${document.attachmentFileName}.${document.fileExtension}`
      : document.attachmentFileName,
  ].filter(Boolean);

  const uploadIndex = uploads.findIndex((upload, index) => {
    if (matchedIndexes.has(index)) return false;
    return (
      candidateNames.includes(upload?.fileName) ||
      candidateNames.includes(upload?.url)
    );
  });

  if (uploadIndex === -1) return null;
  matchedIndexes.add(uploadIndex);
  return uploads[uploadIndex];
};

// How many files go up at once. Enough to keep a batch moving without
// exhausting the browser's per-host connection pool.
export const UPLOAD_CONCURRENCY = 4;

// PUT one file to storage and build its entry for the attach payload.
const putDocument = async (document, uploadData) => {
  if (!uploadData?.signedUrl) {
    throw new Error(`Failed to prepare upload URL for ${document.fileName}`);
  }

  const uploadResponse = await fetch(uploadData.signedUrl, {
    method: "PUT",
    headers: { "Content-Type": document.contentType },
    body: document.file,
  });

  if (!uploadResponse.ok) {
    throw new Error(
      `Failed to upload ${document.fileName}: ${uploadResponse.statusText}`
    );
  }

  // The signed URL path starts with the bucket segment; the attach payload
  // expects the object key without it.
  const parsedUrl = new URL(uploadData.signedUrl);
  const fileUrl = parsedUrl.pathname.replace(/^\/[^/]+\//, "");

  return {
    fileName: document.attachmentFileName,
    fileUrl,
    fileExtension: document.fileExtension,
    contentType: document.contentType,
    fileAccessType: document.fileAccessType,
  };
};

// Ask for every signed URL in one call, then push the files up in parallel.
// A file that fails does not sink the batch: it comes back under `failed` so the
// caller can attach what made it and leave the rest pending for another try.
// Throws only when the whole batch is unusable (the metadata call failed).
export const uploadDocuments = async (
  pendingDocuments = [],
  { concurrency = UPLOAD_CONCURRENCY } = {}
) => {
  const documentsToUpload = pendingDocuments.filter((el) => el?.file);
  if (!documentsToUpload.length) return { uploaded: [], failed: [] };

  const metadataResponse = await createDocumentMetadataBatch({
    items: documentsToUpload.map(({ contentType, fileExtension, fileName }) => ({
      contentType,
      fileExtension,
      fileName,
    })),
  });

  if (!metadataResponse || metadataResponse.status > 204) {
    const failedMsg = metadataResponse?.data?.statusCode
      ? `${metadataResponse.data.statusCode}: ${metadataResponse.data.message}`
      : "Failed to prepare file uploads";
    throw new Error(failedMsg);
  }

  // Pair each document with its signed URL up front: matching consumes indexes
  // in order, so it must not race with the parallel uploads below.
  const uploads = metadataResponse?.data?.uploads || [];
  const matchedIndexes = new Set();
  const queue = documentsToUpload.map((document) => ({
    document,
    uploadData: findUploadForDocument(document, uploads, matchedIndexes),
  }));

  const uploaded = [];
  const failed = [];
  const workers = Array.from(
    { length: Math.min(Math.max(concurrency, 1), queue.length) },
    async () => {
      while (queue.length) {
        const { document, uploadData } = queue.shift();
        try {
          uploaded.push({ document, file: await putDocument(document, uploadData) });
        } catch (err) {
          failed.push({ document, error: err.message });
        }
      }
    }
  );
  await Promise.all(workers);

  return { uploaded, failed };
};

export const attachDocumentsToAssignment = ({ assignmentId, files }) =>
  attachFilesToAssignments({ assignmentIds: [assignmentId], files });

// Upload whatever the user added in the form, then connect the stored files to
// the (already saved) assignment in one call. Returns the documents that made it
// (`attached`) alongside the ones that did not (`failed`), so the caller can
// keep the failures pending and let the user save again.
export const uploadAndAttachDocuments = async ({
  assignmentId,
  pendingDocuments,
  concurrency,
}) => {
  const { uploaded, failed } = await uploadDocuments(pendingDocuments, {
    concurrency,
  });
  if (!uploaded.length) return { attached: [], failed };

  const response = await attachDocumentsToAssignment({
    assignmentId,
    files: uploaded.map((el) => el.file),
  });
  if (!response || response.status > 204) {
    // The files are in storage but not linked to the assignment. Fail loudly so
    // nothing is marked as done and the next save retries the whole batch.
    const failedMsg = response?.data?.statusCode
      ? `${response.data.statusCode}: ${response.data.message}`
      : "Failed to attach files to the assignment";
    throw new Error(failedMsg);
  }

  return { attached: uploaded.map((el) => el.document), failed };
};

// /assignment-document/{id} answers with the file as base64, so the bytes have to
// be rebuilt before the browser can save them.
const base64ToBlob = (base64, contentType) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: contentType || "application/octet-stream" });
};

// Save one of the documents listed in the Notes & Files tab. A file the user only
// just picked is still in memory and goes straight to disk; one that is already on
// the assignment has to be fetched first. Never throws — the caller flashes
// whatever comes back under `error`.
export const downloadAssignmentDocument = async (document) => {
  if (document?.file) {
    saveAs(document.file, document.fileName || document.file.name);
    return { ok: true };
  }

  // Freshly attached documents are marked uploaded before the assignment is
  // re-read, so they carry no id yet — the reopened record is what fills it in.
  if (!document?.id) {
    return {
      ok: false,
      error: document?.uploaded
        ? `${document.fileName} was just attached — reopen the assignment to download it.`
        : `${document?.fileName || "This file"} is not stored yet — save the assignment first.`,
    };
  }

  const response = await getNewAssignmentFile(document.id);
  if (!response || response.status > 204 || !response.data?.content) {
    return {
      ok: false,
      error:
        response?.data?.message ||
        response?.data?.error ||
        `Failed to download ${document.fileName}`,
    };
  }

  const { content, contentType, fileName, fileExtension } = response.data;
  // Prefer the name the tab is showing; fall back to the one the API reports.
  const savedName =
    document.fileName || [fileName, fileExtension].filter(Boolean).join(".");
  saveAs(base64ToBlob(content, contentType || document.contentType), savedName);
  return { ok: true };
};

// Detach a file that is already stored against the assignment. A document that
// never left the form has nothing to clean up, so it resolves as done.
//
// `notSupported` is set when the API answers 404/405, which is what a deployment
// without the removal route looks like — the tab says that plainly instead of
// showing a bare status code.
export const detachAssignmentDocument = async (document) => {
  if (!document?.id) return { ok: true };

  const response = await deleteAssignmentDocument(document.id);
  if (!response || response.status > 204) {
    return {
      ok: false,
      notSupported: response?.status === 404 || response?.status === 405,
      error:
        response?.data?.message ||
        response?.data?.error ||
        `Failed to remove ${document.fileName}`,
    };
  }

  return { ok: true };
};
